import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";

const medLabel = (m) =>
  [m?.name || m?.drug_name, m?.dose, m?.frequency].filter(Boolean).join(" · ") ||
  "Unnamed medication";

/**
 * Pick which administered medications the patient continues at home.
 *
 * Replaces an automatic copy of every medication ever ordered into
 * advices.medications_to_continue — a merge with no stop-date and no status
 * filter, so stopped drugs, one-off pre-op antibiotics and PRNs were all
 * proposed as take-home medication.
 *
 * Nothing is pre-selected: continuing a drug after discharge is a decision, not
 * a default.
 */
const CopyMedicationsDialog = ({ open, source = [], onClose, onConfirm }) => {
  const [checked, setChecked] = useState({});

  useEffect(() => {
    if (open) setChecked({});
  }, [open]);

  const toggle = (i) => setChecked((prev) => ({ ...prev, [i]: !prev[i] }));
  const selected = source.filter((_, i) => checked[i]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Copy from administered medications</DialogTitle>
      <DialogContent dividers>
        {source.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No administered medications recorded for this admission.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select the medications the patient should continue after discharge.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {source.map((m, i) => (
                <FormControlLabel
                  key={`copy-med-${i}`}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(checked[i])}
                      onChange={() => toggle(i)}
                    />
                  }
                  label={<Typography variant="body2">{medLabel(m)}</Typography>}
                />
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(selected)}
          variant="contained"
          disabled={selected.length === 0}
        >
          Add {selected.length || ""} medication{selected.length === 1 ? "" : "s"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyMedicationsDialog;
